import { useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE } from "./utils";
import { getAuthHeaders } from "./auth-fetch";

// const API_BASE = typeof window !== "undefined" ? "" : "http://localhost:8001";

function localKey(key) {
  return `aivoryems.state.${key}`;
}

async function fetchServerState(key) {
  const res = await fetch(`${API_BASE}/api/state/${encodeURIComponent(key)}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return null;
  const payload = await res.json();
  return payload?.value ?? null;
}

export function usePersistedState(key, initialValue) {
  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(localKey(key));
      return raw ? JSON.parse(raw) : initialValue;
    } catch {
      return initialValue;
    }
  });
  const [status, setStatus] = useState("idle");

  const refetch = useCallback(async () => {
    try {
      const serverValue = await fetchServerState(key);
      if (serverValue) {
        setValue(serverValue);
        localStorage.setItem(localKey(key), JSON.stringify(serverValue));
      }
    } catch {
      // keep local state
    }
  }, [key]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const serverValue = await fetchServerState(key);
        if (mounted && serverValue) {
          setValue(serverValue);
          localStorage.setItem(localKey(key), JSON.stringify(serverValue));
        }
      } catch {
        // silent fallback to local state
      }
    })();
    return () => {
      mounted = false;
    };
  }, [key]);

  useEffect(() => {
    const onExternal = (ev) => {
      if (ev?.detail?.key === key) refetch();
    };
    window.addEventListener("aivory-state-updated", onExternal);
    const shouldRefetchOnFocus = key.startsWith("page-builder.");
    const onFocus = () => {
      if (shouldRefetchOnFocus) refetch();
    };
    if (shouldRefetchOnFocus) window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("aivory-state-updated", onExternal);
      if (shouldRefetchOnFocus) window.removeEventListener("focus", onFocus);
    };
  }, [key, refetch]);

  useEffect(() => {
    const t = window.setTimeout(async () => {
      try {
        setStatus("saving");
        localStorage.setItem(localKey(key), JSON.stringify(value));
        await fetch(`${API_BASE}/api/state/${encodeURIComponent(key)}`, {
          method: "PUT",
          headers: { "content-type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({ value }),
        });
        setStatus("saved");
        window.setTimeout(() => setStatus("idle"), 1200);
      } catch {
        setStatus("error");
      }
    }, 500);
    return () => window.clearTimeout(t);
  }, [key, value]);

  const statusLabel = useMemo(() => {
    if (status === "saving") return "Saving...";
    if (status === "saved") return "Saved";
    if (status === "error") return "Save failed";
    return null;
  }, [status]);

  return { value, setValue, status, statusLabel };
}

